import type { ReactNode } from "react";
import {
  Dialog as AriaDialog,
  Button as AriaButton,
  Heading,
  Modal,
  ModalOverlay,
  type ModalOverlayProps,
} from "react-aria-components";
import { useTranslation } from "react-i18next";
import { Icon } from "@core/assets";
import { cn } from "@core/utils";

interface BottomSheetProps extends Omit<
  ModalOverlayProps,
  "className" | "children"
> {
  /** Panel title — the labelling `<h2>`, never omitted. */
  title: string;
  /** Supporting copy under the title (e.g. "0 of 3 required documents sent"). */
  description?: string;
  children?: ReactNode;
  /** Footer content, pinned below the scrolling body (e.g. an offline-availability note). */
  footer?: ReactNode;
  /** Inline max width from `sm:` up; full-bleed at 320px regardless. */
  className?: string;
}

/**
 * Bottom sheet primitive on React Aria's `Modal` — same foundation `Drawer` uses (focus trap,
 * scroll lock, Escape/overlay dismiss, `aria-labelledby`), anchored to the bottom edge instead of
 * the side. `Drawer` stays a right-side panel for anything that reads as a workspace tool; this is
 * for content that belongs to the screen behind it and is meant to feel like sliding it up rather
 * than opening beside it (Figma's own name for the pattern is literally "feuille" — sheet).
 *
 * First real consumer: the Documents sheet (US-E3-08 §3.3) — triggered from a small button in the
 * persistent bottom action bar, showing the round's document requirements as a plain list rather
 * than the wizard's own full-width panel styling.
 */
export function BottomSheet({
  title,
  description,
  children,
  footer,
  className,
  ...props
}: BottomSheetProps) {
  const { t } = useTranslation("common");

  return (
    <ModalOverlay
      isDismissable
      className="fixed inset-0 z-50 flex items-end justify-center bg-overlay backdrop-blur-[2px] entering:animate-in entering:fade-in exiting:animate-out exiting:fade-out"
      {...props}
    >
      <Modal
        className={cn(
          "max-h-[85vh] w-full rounded-t-2xl bg-surface-card shadow-lg outline-none sm:max-w-120 sm:rounded-2xl sm:mb-lg",
          className
        )}
      >
        <AriaDialog className="flex max-h-[85vh] flex-col outline-none">
          {({ close }) => (
            <>
              <div className="flex shrink-0 justify-center pb-sm pt-xs">
                <div className="h-1 w-9 rounded-full bg-gray-300" />
              </div>
              <div className="flex shrink-0 items-start justify-between gap-xs px-md pb-md">
                <div className="flex min-w-0 flex-1 flex-col gap-xxs">
                  <Heading
                    slot="title"
                    className="text-lg-semibold text-(--text-primary)"
                  >
                    {title}
                  </Heading>
                  {description ? (
                    <p className="text-xs-regular text-(--text-tertiary)">
                      {description}
                    </p>
                  ) : null}
                </div>
                <AriaButton
                  onPress={close}
                  aria-label={t("actions.dismiss")}
                  className={
                    "flex size-11 shrink-0 items-center justify-center rounded-md " +
                    "text-(--text-secondary) outline-none transition-colors " +
                    "hover:bg-gray-50 pressed:bg-gray-100 " +
                    "focus-visible:ring-2 focus-visible:ring-focus-ring"
                  }
                >
                  <Icon name="close" size={20} />
                </AriaButton>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-md">
                {children}
              </div>

              {footer ? (
                <div className="shrink-0 px-md py-md">{footer}</div>
              ) : null}
            </>
          )}
        </AriaDialog>
      </Modal>
    </ModalOverlay>
  );
}

export type { BottomSheetProps };
